import React from 'react';
import './SubscriptionCatalogPanel.css';
import './SubscriptionHubPanel.css';

export type SubscriptionHubTarget =
  | 'subscriptionLevels'
  | 'payingSubscriptions'
  | 'expiredSubscriptions'
  | 'mySubscriptions';

type SubscriptionHubPanelProps = {
  language: 'en' | 'es';
  canManageCatalog: boolean;
  onOpen: (target: SubscriptionHubTarget) => void;
};

const copy = {
  en: {
    title: 'Subscriptions',
    lede: 'Pick where to work. Plans set what a paying account can use. Paying and expired lists are the commercial records. mySubscriptions is what you belong to.',
    levels: 'Subscription levels',
    levelsBody: 'Edit plan names, prices, and which NextCompli features each plan includes.',
    paying: 'Paying subscriptions',
    payingBody: 'Active commercial accounts, people on each account, plan changes, and renewals.',
    expired: 'Expired subscriptions',
    expiredBody: 'Accounts whose plan ended or whose payment is past due.',
    mine: 'mySubscriptions',
    mineBody: 'The subscription accounts you already belong to.',
    open: 'Open',
  },
  es: {
    title: 'Suscripciones',
    lede: 'Elija dónde trabajar. Los planes definen qué puede usar una cuenta de pago. Las listas de pago y vencidas son el registro comercial. mySubscriptions es a lo que usted pertenece.',
    levels: 'Niveles de suscripción',
    levelsBody: 'Edite nombres, precios y qué funciones de NextCompli incluye cada plan.',
    paying: 'Suscripciones de pago',
    payingBody: 'Cuentas comerciales activas, personas en cada cuenta, cambios de plan y renovaciones.',
    expired: 'Suscripciones vencidas',
    expiredBody: 'Cuentas cuyo plan terminó o cuyo pago está atrasado.',
    mine: 'mySubscriptions',
    mineBody: 'Las cuentas de suscripción a las que usted ya pertenece.',
    open: 'Abrir',
  },
};

const SubscriptionHubPanel: React.FC<SubscriptionHubPanelProps> = ({
  language,
  canManageCatalog,
  onOpen,
}) => {
  const text = copy[language];
  const cards: Array<{
    id: SubscriptionHubTarget;
    title: string;
    body: string;
    visible: boolean;
  }> = [
    {
      id: 'subscriptionLevels',
      title: text.levels,
      body: text.levelsBody,
      visible: canManageCatalog,
    },
    {
      id: 'payingSubscriptions',
      title: text.paying,
      body: text.payingBody,
      visible: canManageCatalog,
    },
    {
      id: 'expiredSubscriptions',
      title: text.expired,
      body: text.expiredBody,
      visible: canManageCatalog,
    },
    {
      id: 'mySubscriptions',
      title: text.mine,
      body: text.mineBody,
      visible: true,
    },
  ];

  return (
    <section className="dashboard-settings-sections dashboard-settings-sections-wide sub-hub" aria-label={text.title}>
      <article className="card settings-card sub-hub__card">
        <div className="settings-card-header">
          <h2>{text.title}</h2>
        </div>
        <p className="dashboard-panel-subtitle sub-hub__lede">{text.lede}</p>
        <div className="sub-hub__grid" role="list">
          {cards.filter((card) => card.visible).map((card) => (
            <button
              key={card.id}
              type="button"
              className="sub-hub__tile"
              role="listitem"
              onClick={() => onOpen(card.id)}
            >
              <strong>{card.title}</strong>
              <span>{card.body}</span>
              <em>{text.open}</em>
            </button>
          ))}
        </div>
      </article>
    </section>
  );
};

export default SubscriptionHubPanel;
